/**
 * Retorna o valor de background CSS baseado no layoutConfig.
 * Se type === "default" ou layoutConfig nulo, retorna null (usa padrão do template).
 */
export function getBackground(layoutConfig) {
  if (!layoutConfig?.background || layoutConfig.background.type === "default") return null;

  if (layoutConfig.background.type === "solid") {
    const { color = "#ffffff", opacity = 100 } = layoutConfig.background.solid ?? {};
    if (opacity >= 100) return color;
    const hex = color.replace("#", "");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${(opacity / 100).toFixed(2)})`;
  }

  if (layoutConfig.background.type === "gradient") {
    const { direction = "to bottom", stops = [] } = layoutConfig.background.gradient ?? {};
    if (stops.length < 2) return null;
    const stopsStr = stops.map((s) => `${s.color} ${s.position}%`).join(", ");
    return `linear-gradient(${direction}, ${stopsStr})`;
  }

  return null;
}

const ADDRESS_DISPLAY_MODES = new Set(["text", "pin", "pinText"]);
const ADDRESS_ALIGNMENTS = new Set(["left", "center", "right", "justify"]);
const ADDRESS_PIN_BORDER_MODES = new Set(["default", "none", "custom"]);
export const RSVP_FONT_OPTIONS = [
  { value: "'Poppins', sans-serif", label: "Poppins" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "'Trebuchet MS', sans-serif", label: "Trebuchet" },
  { value: "Arial, sans-serif", label: "Arial" },
  { value: "'Courier New', monospace", label: "Courier New" },
  { value: "'Brush Script MT', cursive", label: "Cursiva" },
];
const RSVP_FONT_FAMILY_SET = new Set(RSVP_FONT_OPTIONS.map((option) => option.value));

export function getAddressDisplayMode(layoutConfig, fallback = "pinText") {
  const value = layoutConfig?.addrDisplayMode;
  return ADDRESS_DISPLAY_MODES.has(value) ? value : fallback;
}

export function getAddressAlignment(layoutConfig, fallback = "left") {
  const value = layoutConfig?.addrAlign;
  return ADDRESS_ALIGNMENTS.has(value) ? value : fallback;
}

export function getTextAlignValue(alignment) {
  return alignment === "justify" ? "justify" : alignment;
}

export function getFlexJustifyValue(alignment, multipleItems = true) {
  if (alignment === "center") return "center";
  if (alignment === "right") return "flex-end";
  if (alignment === "justify") return multipleItems ? "space-between" : "center";
  return "flex-start";
}

export function getAddressPinBorderMode(layoutConfig, fallback = "default") {
  const value = layoutConfig?.addrPinBorderMode;
  return ADDRESS_PIN_BORDER_MODES.has(value) ? value : fallback;
}

export function sanitizeRsvpFontFamily(value) {
  return RSVP_FONT_FAMILY_SET.has(value) ? value : null;
}

const CIRCLE_SIZES = { P: 90, M: 140, G: 200 };
const SQUARE_SIZES = { P: 140, M: 220, G: 320 };

/**
 * Retorna os estilos inline para o elemento <img> da imagem de capa.
 * Retorna null se layoutConfig não definir coverImage.
 */
export function getCoverImageStyle(layoutConfig) {
  if (!layoutConfig?.coverImage) return null;
  const { shape = "circle", size = "M" } = layoutConfig.coverImage;
  const dim = shape === "circle" ? CIRCLE_SIZES[size] ?? CIRCLE_SIZES.M : null;
  const squareDim = SQUARE_SIZES[size] ?? SQUARE_SIZES.M;

  if (shape === "circle") {
    return { width: dim, height: dim, borderRadius: "50%", objectFit: "cover", display: "block" };
  } else {
    return { width: squareDim, maxWidth: "100%", height: "auto", borderRadius: 12, objectFit: "cover", display: "block" };
  }
}

/**
 * Retorna os estilos para o wrapper da imagem de capa.
 * Para círculo: div centralizado com dimensões fixas.
 * Para quadrado: div centralizado com maxWidth.
 */
export function getCoverImageWrapperStyle(layoutConfig) {
  if (!layoutConfig?.coverImage) return null;
  const { shape = "circle", size = "M" } = layoutConfig.coverImage;
  const dim = shape === "circle" ? CIRCLE_SIZES[size] ?? CIRCLE_SIZES.M : null;
  const squareDim = SQUARE_SIZES[size] ?? SQUARE_SIZES.M;

  if (shape === "circle") {
    return { width: dim, height: dim, borderRadius: "50%", overflow: "hidden", margin: "0 auto" };
  } else {
    return { width: squareDim, maxWidth: "100%", borderRadius: 12, overflow: "hidden", margin: "0 auto" };
  }
}

export const defaultLayoutConfig = {
  coverImage: { shape: "circle", size: "M" },
  background: {
    type: "default",
    solid: { color: "#ffffff", opacity: 100 },
    gradient: {
      direction: "to bottom",
      stops: [
        { color: "#ffffff", position: 0 },
        { color: "#cccccc", position: 100 },
      ],
    },
  },
  sections: { howToGet: true, address: true },
  bannerUrl: null,
  headerBg: null,
  headerTitleColor: null,
  dateBg: null,
  dateTextColor: null,
  dateDividerColor: null,
  addrBg: null,
  addrTextColor: null,
  addrDividerColor: null,
  addrDisplayMode: null,
  addrAlign: null,
  addrPinBg: null,
  addrPinBorderMode: null,
  addrPinBorderColor: null,
  rsvpBg: null,
  rsvpTextColor: null,
  rsvpSectionBg: null,
  rsvpSectionTitleColor: null,
  rsvpPeopleBg: null,
  rsvpPeopleTextColor: null,
  rsvpPeopleTitleColor: null,
  rsvpPeopleFontFamily: null,
  rsvpPeopleInputBg: null,
  rsvpPeopleInputTextColor: null,
  rsvpPeopleInputPlaceholderColor: null,
  rsvpMessageBg: null,
  rsvpMessageTextColor: null,
  rsvpMessageTitleColor: null,
  rsvpMessageFontFamily: null,
  rsvpMessageInputBg: null,
  rsvpMessageInputTextColor: null,
  rsvpMessageInputPlaceholderColor: null,
  rsvpClosedBg: null,
  rsvpClosedTitleColor: null,
  rsvpClosedTitleWeight: null,
  rsvpClosedTextColor: null,
  rsvpAddButtonBg: null,
  rsvpAddButtonTextColor: null,
  rsvpSubmitButtonBg: null,
  rsvpSubmitButtonTextColor: null,
  rsvpInputBg: null,
  rsvpInputTextColor: null,
  rsvpInputPlaceholderColor: null,
};
